import React, { ReactNode } from "react";
import KuikaBadge, { KuikaBadgeColor } from "../kuika-badge/kuika-badge";

// Every role has one colour, and it is the same colour on every surface that shows it: the
// Users table, My profile, the share dialog. Roles reach the UI as the backend's display
// names, so that is what the map is keyed by — which also keeps the ui-kit free of the model
// package. The test pins it against both role enums, so a role added there without a colour
// here fails the build rather than quietly turning grey.
//
// Families share a hue: every owner is blue, the developer roles are orange, the design roles
// pink. Red stays out — on a badge it reads as an error, not as a role.
const ROLE_BADGE_COLORS: Readonly<Record<string, KuikaBadgeColor>> = {
  workspaceowner: "blue",
  projectowner: "blue",
  member: "green",
  supervisor: "purple",
  financemanager: "yellow",
  developer: "orange",
  componentdeveloper: "orange",
  devops: "orange",
  designer: "pink",
  componentdesigner: "pink",
  designops: "pink",
  viewer: "grey"
};

/**
 * The lookup key for a role name. The backend seeds "Design Ops" where the enum member is
 * `DesignOps`, so case and anything that is not a letter or digit are dropped.
 */
export function normalizeRoleName(roleName: string): string {
  return roleName.toLowerCase().replace(/[^a-z0-9]/g, "");
}

/**
 * The badge colour for a role, by its whole name — never a fragment of it. A name this map
 * does not know is grey, which is what a custom or unrecognised role should look like.
 */
export function getRoleBadgeColor(roleName: string | undefined): KuikaBadgeColor {
  if (!roleName) return "grey";
  return ROLE_BADGE_COLORS[normalizeRoleName(roleName)] ?? "grey";
}

export interface KuikaRoleBadgeProps {
  /** The role's display name as the backend sends it. Decides the colour, and is the text unless `label` is given. */
  role: string;
  /** Different text for the same role (a column that only has room for "Owner"). Never changes the colour. */
  label?: string;
  /** Drawn after the text — the chevron when the badge is a role picker's trigger. */
  rightIcon?: ReactNode;
  /**
   * Animates a change of role in place: the colour fades over, the name swaps with a short blur and
   * the badge's width follows (KuikaBadge `animateChanges`). Off by default — a badge re-used for
   * another row (sorting, paging) must not pretend the role just changed. Turn it on where the same
   * user's role really changes in front of the viewer, such as a role picker's trigger.
   */
  animateChanges?: boolean;
  dataTestId?: string;
}

/**
 * A user's workspace or project role. Size and weight are fixed so that the same role looks
 * identical wherever it appears; only the colour varies, and only by role.
 */
const KuikaRoleBadge: React.FC<KuikaRoleBadgeProps> = ({ role, label, rightIcon, animateChanges, dataTestId }) => (
  <KuikaBadge
    content={label ?? role}
    color={getRoleBadgeColor(role)}
    variant="subtle"
    size="small"
    rightIcon={rightIcon}
    animateChanges={animateChanges}
    dataTestId={dataTestId}
  />
);

export default KuikaRoleBadge;
